<template>
    <div class="schedule-form">
        <div
            class="cursor-pointer"
            @click="openModal"
        >
            <div class="flex items-center text-primary schedule-add-button" v-if="!hideButton">
                <i class="icon-base add-schedule-btn-text" :class="icon" v-if="showIcon" />
                <span class="text-main-sm leading-4">
                    {{ buttonLabel }}
                </span>
            </div>
        </div>
        <modal
            v-show="showDialog"
            :visible.sync="showDialog"
            width="90%"
            top="3vh"
            wrapper-classes="full-height schedule-from-modal"
        >
            <template v-slot:title>
                <div class="flex w-full text-xl px-2">
                    {{ title }}
                </div>
            </template>
            <div class="relative">
                <div class="" v-if="showDialog">
                    <div class="flex w-full justify-between text-sm leading-5 px-8 pt-4">
                        <div class="main-text-color">
                            {{ getActiveStep }}
                        </div>
                        <div>
                            <el-tooltip
                                class="item"
                                effect="dark"
                                :content="$t(`reportSchedule.${helpInfo[currentStep]}.help`)"
                                placement="bottom"
                            >
                                <span class="flex items-center cursor-pointer">
                                    <i
                                        class="vc-icon-info text-primary text-xl mx-2"
                                    />
                                    <div class="break-normal text-primary">{{ $t('general.help') }}</div>
                                </span>
                            </el-tooltip>
                        </div>
                    </div>
                    <fade-transition
                        :duration="250"
                        mode="out-in">
                        <component
                            :is="getStepComponent"
                            :key="currentStep"
                            v-bind="$attrs"
                            @on-update="onUpdateStepData"
                            :reportItemData="data"
                            @on-back="onBack"
                            @on-finish="onFinish"
                            class="step-wrapper"
                            :reportId="reportId"
                            :create="create"
                            :scheduleData="schedule"
                            :dataToEdit="dataToEdit"
                            :reportData="reportData"
                            @updateScheduleData="updateScheduleData"
                            @pushDataInReportTrigger="pushDataInReportTrigger"
                            @updateSchedule="updateSchedule"
                            @createNewCriteria="createNewCriteria"
                            @createNewCondition="createNewCondition"
                            @deleteCondition="deleteCondition"
                            @deleteCriteria="deleteCriteria"
                            @updateReportTriggerItem="updateReportTriggerItem"
                        />
                    </fade-transition>
                </div>
            </div>
            <template #footer>
                <div
                    class="main-bg-light border-t-2 px-16 border-gray-300 bottom-0 h-20 flex w-full items-center justify-between">
                    <base-button
                        class="mx-4"
                        outline
                        fixed-width="w-37"
                        @click="onCancel"
                    >
                        <div class="flex items-center">
                            <IconDiscard class="mx-1"/>
                            <span class="mx-1 text-base font-bold">{{ $t('common.cancel') }}</span>
                        </div>
                    </base-button>
                    <div class="flex items-center">
                        <portal-target name="next-button">
                            <base-button
                                fixed-width="w-37"
                                type="primary"
                                @click="onNext"
                            >
                                <div class="flex items-center">
                                    <span class="mx-1 text-base font-bold">{{ $t('general.next') }}</span>
                                    <IconDirRight v-if="!$rtl.isRTL" class="mx-1" />
                                    <IconDirLeft v-else class="mx-1" />
                                </div>
                            </base-button>
                        </portal-target>
                        <portal-target name="additional-actions"/>
                    </div>
                </div>
            </template>
        </modal>
    </div>
</template>
<script>
    const wizardLength = 3
    import { Notification } from 'element-ui'
    import cloneDeep from 'lodash/cloneDeep'
    import { setDefaultReportTriggerData, setDefaultReportConditionData } from '@/enum/reportStaticData'

    export default {
        props: {
            data: {
                type: Object,
                default: () => ({})
            },
            buttonLabel: {
                type: String,
                default: ''
            },
            icon: {
                type: String,
                default: ''
            },
            reportId: {
                type: [Number, String],
                default: ''
            },
            dataToEdit: {
                type: Object,
                default: () => ({})
            },
            title: {
                type: String,
                default: ''
            },
            create: {
                type: Boolean,
                default: false
            },
            reportData: {
                type: Object,
                default: () => ({})
            },
            hideButton: {
                type: Boolean,
                default: false
            },
            showIcon: {
                type: Boolean,
                default: true
            }
        },
        components: {
            Modal: () => import('@/components/Common/Modal'),
            Step0: () => import('@/modules/reports/components/schedule/components/Step0'),
            Step1: () => import('@/modules/reports/components/schedule/components/Step1'),
            Step2: () => import('@/modules/reports/components/schedule/components/Step2')
        },
        data() {
            return {
                showDialog: false,
                currentStep: 0,
                drawWizard: true,
                wizardData: {},
                schedule: setDefaultReportTriggerData(),
                helpInfo: {
                    0: 'frequency',
                    1: 'conditions',
                    2: 'recipients'
                }
            }
        },
        watch: {
            async showDialog (val) {
                if (!val) {
                    this.schedule = setDefaultReportTriggerData()
                    this.currentStep = 0
                }
            }
        },
        computed: {
            getStepComponent() {
                return `Step${this.currentStep}`
            },
            getActiveStep() {
                return this.$t('general.step') + ' ' + (this.currentStep + 1) + ' ' + this.$t('general.of') + ' ' + wizardLength
            }
        },
        methods: {
            onCancel() {
                this.showDialog = false
            },
            onNext() {
                if (this.currentStep === 2) {
                    return
                }

                this.currentStep++
            },
            onBack() {
                if (this.currentStep === 0) {
                    return
                }
                this.currentStep--
            },
            onUpdateStepData(data = {}) {
                const { nextStep: nextStep = false } = data
                if (nextStep) {
                    this.onNext()
                }
            },
            onEditStep(stepIndex) {
                this.currentStep = stepIndex
            },
            onFinish () {
                this.showDialog = false
                this.schedule = setDefaultReportTriggerData()
                this.$emit('addedSchedule')
            },
            async openModal () {
                if (!this.data || !this.data.ReportItemList.length) {
                    Notification.warning(this.$t('report.schedule.needAddAtLeastOneWidget'))
                    return
                }
                this.showDialog = true
                const { reportTriggerData } = this.dataToEdit
                if (this.dataToEdit && Object.keys(this.dataToEdit).length) {
                    const isReportDoesNotHaveTriggerCondition = reportTriggerData.ReportTriggerCondition.every(el => !Object.keys(el).length)
                    reportTriggerData.ReportTriggerCondition = reportTriggerData.ReportTriggerCondition
                        .filter(el => {
                            if (el && el.ReportTriggerConditionFilter && el.ReportTriggerConditionFilter.length) {
                                el.ReportTriggerConditionFilter = el.ReportTriggerConditionFilter.filter(el => Object.values(el).every(el => el !== '' && el !== null))
                            }
                            return el && Object.keys(el).length
                        })

                    this.schedule = cloneDeep(reportTriggerData)
                    if (isReportDoesNotHaveTriggerCondition) {
                        this.schedule.ReportTriggerCondition = [
                            {
                                ReportTriggerConditionFilter: [
                                    setDefaultReportConditionData()
                                ]
                            }
                        ]
                    }
                }
            },
            updateScheduleData (data) {
                this.schedule.ScheduleData = data
            },
            updateSchedule (data, keysToUpdate) {
                keysToUpdate.forEach(el => {
                    this.schedule[el] = data[el]
                })
            },
            pushDataInReportTrigger (data) {
                this.$emit('pushDataInReportTrigger', data)
            },
            createNewCriteria () {
                this.schedule.ReportTriggerCondition.push(
                    {
                        ReportTriggerConditionFilter: [
                            setDefaultReportConditionData()
                        ]
                    }
                )
            },
            createNewCondition (index) {
                this.schedule.ReportTriggerCondition[index].ReportTriggerConditionFilter.push(
                    setDefaultReportConditionData()
                )
            },
            deleteCondition (data) {
                const reportTriggerConditionFilter = this.schedule.ReportTriggerCondition[data.groupIndex].ReportTriggerConditionFilter
                if (reportTriggerConditionFilter.length === 1 && reportTriggerConditionFilter[data.itemIndex].WidgetID !== null) {
                    reportTriggerConditionFilter[data.itemIndex].WidgetID = null
                } else {
                    this.schedule.ReportTriggerCondition[data.groupIndex].ReportTriggerConditionFilter.splice(data.itemIndex, 1)
                }
            },
            deleteCriteria (index) {
                this.schedule.ReportTriggerCondition.splice(index, 1)
            },
            updateReportTriggerItem (data) {
                this.$emit('updateReportTriggerItem', data)
            }
        }
    }
</script>

<style lang="scss">
.grid-wrapper {
    @apply bg-white shadow-base rounded-md;
}

.step-wrapper {
    height: 100%;
    min-height: calc(95vh - 215px);
    max-height: calc(95vh - 215px);
    @apply overflow-auto;
}

.transition {
    transition: all 0.3s ease-out;
}

[dir="rtl"] .add-schedule-btn-text {
    @apply ml-2;
}
[dir="ltr"] .add-schedule-btn-text {
    @apply mr-2;
}
</style>
